import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, Globe, Database, HelpCircle, CreditCard, Bell, Eye } from 'lucide-react';

const MainSettingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Only real, routed pages are listed here. Several categories that used to
  // link to /settings/theme, /settings/display, /settings/regional,
  // /settings/sessions, /settings/shortcuts(/customize),
  // and /settings/storage were removed -- none of those pages exist, so every
  // one of those links 404'd. Add them back once the pages are actually built.
  const settingsCategories = [
    {
      id: 'account',
      title: t('settings.categories.account.title'),
      description: t('settings.categories.account.description'),
      icon: <Settings className="w-6 h-6" />,
      items: [
        {
          id: 'profile',
          title: t('settings.items.profile.title'),
          description: t('settings.items.profile.description'),
          path: '/profile'
        },
        {
          id: 'security',
          title: t('settings.items.security.title'),
          description: t('settings.items.security.description'),
          path: '/settings/security'
        }
      ]
    },
    {
      id: 'accessibility',
      title: t('settings.categories.accessibility.title'),
      description: t('settings.categories.accessibility.description'),
      icon: <Eye className="w-6 h-6" />,
      items: [
        {
          id: 'accessibility-settings',
          title: t('settings.items.accessibilitySettings.title'),
          description: t('settings.items.accessibilitySettings.description'),
          path: '/settings/accessibility'
        }
      ]
    },
    {
      id: 'language',
      title: t('settings.categories.language.title'),
      description: t('settings.categories.language.description'),
      icon: <Globe className="w-6 h-6" />,
      items: [
        {
          id: 'language-selection',
          title: t('settings.items.language.title'),
          description: t('settings.items.language.description'),
          path: '/settings/language'
        }
      ]
    },
    {
      id: 'notifications',
      title: t('settings.categories.notifications.title'),
      description: t('settings.categories.notifications.description'),
      icon: <Bell className="w-6 h-6" />,
      items: [
        {
          id: 'notification-settings',
          title: t('settings.items.notificationSettings.title'),
          description: t('settings.items.notificationSettings.description'),
          path: '/settings/notifications'
        }
      ]
    },
    {
      id: 'data',
      title: t('settings.categories.data.title'),
      description: t('settings.categories.data.description'),
      icon: <Database className="w-6 h-6" />,
      items: [
        {
          id: 'manage-data',
          title: t('settings.items.manageData.title'),
          description: t('settings.items.manageData.description'),
          path: '/settings/your-data'
        }
      ]
    },
    {
      id: 'billing',
      title: t('settings.categories.billing.title'),
      description: t('settings.categories.billing.description'),
      icon: <CreditCard className="w-6 h-6" />,
      items: [
        {
          id: 'subscriptions',
          title: t('settings.items.subscriptions.title'),
          description: t('settings.items.subscriptions.description'),
          path: '/settings/subscriptions'
        },
        {
          id: 'creator-dashboard',
          title: t('settings.items.creatorDashboard.title'),
          description: t('settings.items.creatorDashboard.description'),
          path: '/creator/dashboard'
        }
      ]
    },
    {
      id: 'help',
      title: t('settings.categories.help.title'),
      description: t('settings.categories.help.description'),
      icon: <HelpCircle className="w-6 h-6" />,
      items: [
        {
          id: 'help-center',
          title: t('settings.items.helpCenter.title'),
          description: t('settings.items.helpCenter.description'),
          path: '/settings/help-center'
        }
      ]
    }
  ];

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('settings.subtitle')}
            </p>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            {t('settings.backToAccount')}
          </button>
        </div>

        <div className="space-y-6">
          {settingsCategories.map(category => (
            <div 
              key={category.id} 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="text-blue-500 dark:text-blue-400">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {category.title}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {category.description}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {category.items.map(item => (
                  <div 
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                      <div className="text-gray-400">
                        <svg 
                          className="w-5 h-5" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M9 5l7 7-7 7" 
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainSettingsPage;